import { useEffect, useState } from 'react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ChatMarkdown } from '../../../../../components/Chat/ChatMarkdown'
import { type ChatConversation, useChatHistory } from '../../../../../models/agentAdmin'

function extractText(content: unknown): string {
  if (typeof content === 'string') return content
  if (Array.isArray(content)) return content.map(extractText).filter(Boolean).join(' ')
  if (content && typeof content === 'object' && 'content' in content) {
    return extractText((content as Record<string, unknown>).content)
  }
  return ''
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function SessionsTab() {
  const { data: chats, isLoading } = useChatHistory()
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const selected = chats?.find((c) => c.id === selectedId) ?? null

  useEffect(() => {
    if (chats?.length && !selectedId) {
      setSelectedId(chats[0].id)
    }
  }, [chats, selectedId])

  if (isLoading)
    return (
      <div className="grid grid-cols-[260px_1fr] gap-6">
        <div className="space-y-1">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-11 w-full" />
          ))}
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    )

  if (!chats?.length) {
    return (
      <p className="text-gray-500">No agent sessions found. Sessions appear after users chat with Bob.</p>
    )
  }

  return (
    <div className="grid grid-cols-[260px_1fr] gap-6">
      <div className="space-y-1">
        {chats.map((chat: ChatConversation) => (
          <button
            key={chat.id}
            onClick={() => setSelectedId(chat.id)}
            className={`bg-surface w-full rounded-md border px-3 py-2 text-left text-sm transition-colors ${
              selected?.id === chat.id ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
            }`}
          >
            <div className="font-medium">{chat.title || 'Untitled'}</div>
            <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
              <span>{chat.itemCount} messages</span>
              <span>{formatDate(chat.dateCreated)}</span>
            </div>
          </button>
        ))}
      </div>

      {selected ? (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{selected.title || 'Untitled'}</CardTitle>
              <div className="flex items-center gap-3 text-sm text-gray-500">
                <span>{selected.itemCount} messages</span>
                <span>{formatDate(selected.dateCreated)}</span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {selected.items.map((item) => (
                <div
                  key={item.id}
                  className={`rounded-md p-3 text-sm ${item.isFromUser ? 'bg-blue-50' : 'bg-gray-50'}`}
                >
                  <div className="mb-1 flex items-center justify-between text-xs text-gray-500">
                    <span className="font-medium">
                      {item.isFromUser ? item.createdByEmail || 'User' : 'Bob'}
                    </span>
                    <span>{formatDate(item.dateCreated)}</span>
                  </div>
                  {item.isFromUser ? (
                    <div className="whitespace-pre-wrap">{extractText(item.content)}</div>
                  ) : (
                    <ChatMarkdown content={extractText(item.content)} />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <p className="text-gray-500">Select a session from the list to view the transcript.</p>
      )}
    </div>
  )
}
